import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { searchProducts, getPriceAndAvailability, getFacets } from '../api/ingram';

const DEFAULT_PAGE_SIZE = 25;

/**
 * Custom hook for managing Ingram Micro catalog data
 * Handles products, categories, brands, pagination, loading, and error states
 */
export function useIngramCatalog() {
    // URL search params for category filtering
    const [searchParams] = useSearchParams();
    const urlCategory = searchParams.get('category') || '';

    // Products state
    const [products, setProducts] = useState([]);
    // Catalog-wide filter facets. Populated once on mount from /facets
    // endpoint (6-hour cached server-side). No longer computed from the
    // current page's products — that was giving misleading counts like
    // "HP I (7)" that only reflected page 1.
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    // Pagination state
    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalRecords: 0,
        totalPages: 0
    });

    // Search/filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('');

    // Loading and error states
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Ref to track if initial load has happened
    const initialLoadDone = useRef(false);

    // Debounce ref for search
    const searchTimeoutRef = useRef(null);

    // NOTE: `applyLivePrices` used to run here after /search returned to
    // hydrate prices via a second batch call. It's now removed because the
    // backend /search route merges prices AND cached image URLs into the
    // response before returning, so first paint already has real prices
    // and (where cached) real images. See server/routes/catalog.routes.js.

    /**
     * Fetch products from the API
     */
    const fetchProducts = useCallback(async (params = {}) => {
        setIsLoading(true);
        setError(null);

        try {
            const {
                keyword = searchTerm,
                category = selectedCategory,
                vendor = selectedBrand,
                pageNumber = pagination.pageNumber,
                pageSize = pagination.pageSize
            } = params;

            const result = await searchProducts({
                keyword: keyword || undefined,
                category: category || undefined,
                vendor: vendor || undefined,
                pageNumber,
                pageSize
            });

            setProducts(result.products);
            setPagination(result.pagination);

            // Facets no longer come from the search response — they're
            // fetched separately from /api/products/facets on mount.
            // Prices + cached image URLs come from /search directly now.

            return result;
        } catch (err) {
            console.error('Failed to fetch products:', err);
            setError(err.message || 'Failed to load products');
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, selectedCategory, selectedBrand, pagination.pageNumber, pagination.pageSize]);

    /**
     * Refresh prices for current products
     */
    const refreshPrices = useCallback(async () => {
        if (products.length === 0) return;

        try {
            const skus = products.map(p => p.ingramPartNumber).filter(Boolean);
            if (skus.length === 0) return;

            const priceData = await getPriceAndAvailability(skus);

            // Update products with new price data
            setProducts(prevProducts =>
                prevProducts.map(product => {
                    const priceInfo = priceData[product.ingramPartNumber];
                    if (priceInfo) {
                        return {
                            ...product,
                            price: priceInfo.price,
                            priceNumeric: priceInfo.priceNumeric,
                            inStock: priceInfo.inStock,
                            availableQuantity: priceInfo.availableQuantity
                        };
                    }
                    return product;
                })
            );
        } catch (err) {
            console.error('Failed to refresh prices:', err);
        }
    }, [products]);

    /**
     * Handle search with debouncing
     */
    const handleSearch = useCallback((term) => {
        setSearchTerm(term);

        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce search
        searchTimeoutRef.current = setTimeout(() => {
            fetchProducts({
                keyword: term,
                pageNumber: 1 // Reset to first page on new search
            });
        }, 300);
    }, [fetchProducts]);

    // Category + brand handlers accept either a scalar (single-select
     // legacy) or an array (multi-select). Arrays are comma-joined and
     // the backend calls Ingram once per value + merges. Empty selection
     // (empty array OR empty string) clears the filter.
    const normalizeMulti = (v) => {
        if (Array.isArray(v)) return v.length > 0 ? v.join(',') : '';
        return v || '';
    };

    const handleCategoryChange = useCallback((category) => {
        const value = normalizeMulti(category);
        setSelectedCategory(value);
        fetchProducts({
            category: value,
            pageNumber: 1 // Reset to first page on filter change
        });
    }, [fetchProducts]);

    const handleBrandChange = useCallback((brand) => {
        const value = normalizeMulti(brand);
        setSelectedBrand(value);
        fetchProducts({
            vendor: value,
            pageNumber: 1 // Reset to first page on filter change
        });
    }, [fetchProducts]);

    /**
     * Handle page change
     */
    const handlePageChange = useCallback((pageNumber) => {
        fetchProducts({ pageNumber });
    }, [fetchProducts]);

    /**
     * Reset all filters
     */
    const resetFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedBrand('');
        fetchProducts({
            keyword: '',
            category: '',
            vendor: '',
            pageNumber: 1
        });
    }, [fetchProducts]);

    /**
     * Retry fetching after error
     */
    const retry = useCallback(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Initial load and URL category changes
    useEffect(() => {
        if (!initialLoadDone.current) {
            initialLoadDone.current = true;
            if (urlCategory) {
                setSelectedCategory(urlCategory);
            }
            fetchProducts({ pageNumber: 1, category: urlCategory || undefined });
        }
    }, [fetchProducts, urlCategory]);

    // Fetch catalog-wide facets once. The server caches for 6 hours so
    // this is essentially instant on all but the very first request per
    // Lambda container.
    useEffect(() => {
        let cancelled = false;
        getFacets()
            .then((facets) => {
                if (cancelled) return;
                setCategories(facets.categories || []);
                setBrands(facets.brands || []);
            })
            .catch((err) => {
                console.error('Failed to load facets:', err);
            });
        return () => { cancelled = true; };
    }, []);

    // Handle URL category changes after initial load
    useEffect(() => {
        if (initialLoadDone.current && urlCategory !== selectedCategory) {
            setSelectedCategory(urlCategory);
            fetchProducts({ category: urlCategory || undefined, pageNumber: 1 });
        }
    }, [urlCategory, selectedCategory, fetchProducts]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return {
        // Data
        products,
        categories,
        brands,

        // Pagination
        pagination,
        currentPage: pagination.pageNumber,
        totalPages: pagination.totalPages,
        totalProducts: pagination.totalRecords,

        // Search/filter state
        searchTerm,
        selectedCategory,
        selectedBrand,

        // Loading and error
        isLoading,
        error,

        // Actions
        handleSearch,
        handleCategoryChange,
        handleBrandChange,
        handlePageChange,
        resetFilters,
        retry,
        refreshPrices,
        refetch: fetchProducts
    };
}

export default useIngramCatalog;
